export type GetFileRes = {
  url: string;
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  updatedAt: string;
};

export type File = {
  category: string;
  contentType: string;
  fileName: string;
  id: string;
  ownerId: string;
  ownerType: string;
  size: number;
  uploadedAt: string;
  updatedAt: string;
  url: string;
  visibility: string;
};

export type GetFilesRes = {
  files: File[];
};

export const FileStatus = {
  NOT_FOUND: 'NotFound',
  UPLOADING: 'Uploading',
  UPLOADED: 'Uploaded',
  FAILED: 'Failed',
} as const;

export type FileStatusType = (typeof FileStatus)[keyof typeof FileStatus];

/**
 * Mirror of @lib/common FileErrorCodes used by the file service. The BE returns
 * one of these codes alongside `FileStatus.FAILED` so the FE can show a
 * human-readable rejection reason.
 */
export const FileFailureCode = {
  SIZE_TOO_LARGE: 'FILE.VALIDATION.SIZE_TOO_LARGE',
  CONTENT_TYPE_NOT_ALLOWED: 'FILE.VALIDATION.CONTENT_TYPE_NOT_ALLOWED',
  EXTENSION_NOT_ALLOWED: 'FILE.VALIDATION.EXTENSION_NOT_ALLOWED',
  CONTENT_TYPE_EXTENSION_MISMATCH: 'FILE.VALIDATION.CONTENT_TYPE_EXTENSION_MISMATCH',
  MAGIC_BYTE_MISMATCH: 'FILE.VALIDATION.MAGIC_BYTE_MISMATCH',
} as const;

export type FileFailureCodeType = (typeof FileFailureCode)[keyof typeof FileFailureCode];

export const FILE_FAILURE_MESSAGES: Record<string, string> = {
  [FileFailureCode.SIZE_TOO_LARGE]: 'File is larger than the 5 MB limit.',
  [FileFailureCode.CONTENT_TYPE_NOT_ALLOWED]: 'This file type is not allowed.',
  [FileFailureCode.EXTENSION_NOT_ALLOWED]: 'This file extension is not allowed.',
  [FileFailureCode.CONTENT_TYPE_EXTENSION_MISMATCH]:
    'File extension does not match its declared type.',
  [FileFailureCode.MAGIC_BYTE_MISMATCH]: "File contents don't match its declared type.",
};

export const getFileFailureMessage = (code?: string): string =>
  (code && FILE_FAILURE_MESSAGES[code]) || 'Upload was rejected. Please try a different file.';

export type UploadRequestResponse = {
  fileId: string;
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  visibility?: 'private' | 'public';
  expiresAt?: string;
};
